import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiX, 
  FiUser, 
  FiMail, 
  FiPhone, 
  FiCalendar, 
  FiMapPin, 
  FiHome,
  FiLock,
  FiStar,
  FiAward,
  FiShield,
  FiCheckCircle,
  FiClock,
  FiGlobe
} from 'react-icons/fi';
import { 
  FaUserCircle, 
  FaCity, 
  FaBuilding,
  FaIdCard,
  FaRegCalendarAlt,
  FaPhoneAlt,
  FaEnvelope
} from 'react-icons/fa';

const CustomerDetailsModal = ({ customer, isOpen, onClose }) => {
  if (!customer) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return {
        icon: <FiCheckCircle className="w-3 h-3" />,
        text: 'Active',
        color: 'bg-emerald-100 text-emerald-700 border-emerald-200'
      };
    } else {
      return {
        icon: <FiClock className="w-3 h-3" />,
        text: 'Inactive',
        color: 'bg-gray-100 text-gray-700 border-gray-200'
      };
    }
  };

  const statusBadge = getStatusBadge(customer.status);

  const InfoCard = ({ icon: Icon, label, value, color = 'indigo' }) => (
    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:shadow-md transition-all">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-${color}-100 to-${color}-50 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        <div className="flex-1">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
          <p className="text-gray-800 font-semibold mt-0.5 break-all">{value || 'Not provided'}</p>
        </div>
      </div>
    </div>
  );

  const SectionHeader = ({ icon: Icon, title, count }) => (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500">
          <Icon className="w-4 h-4 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
      </div>
      {count !== undefined && (
        <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
          {count}
        </span>
      )}
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Header with Gradient */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                      <FaUserCircle className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">{customer.customerName}</h2>
                      <p className="text-indigo-100 text-sm mt-0.5">Customer since {formatDateShort(customer.createdAt)}</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white/80 hover:text-white hover:bg-white/20 rounded-xl p-2 transition-all"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                {/* Status Badge */}
                <div className="mb-6 flex justify-end">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${statusBadge.color}`}>
                    {statusBadge.icon}
                    {statusBadge.text}
                  </span>
                </div>

                {/* Basic Information Section */}
                <div className="mb-8">
                  <SectionHeader icon={FiUser} title="Basic Information" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoCard icon={FaUserCircle} label="Full Name" value={customer.customerName} color="indigo" />
                    <InfoCard icon={FaEnvelope} label="Email Address" value={customer.customerEmail} color="blue" />
                    <InfoCard icon={FaPhoneAlt} label="Phone Number" value={customer.customerPhone} color="green" />
                    <InfoCard icon={FiLock} label="Password" value={customer.password} color="amber" />
                    <InfoCard icon={FaRegCalendarAlt} label="Joined Date" value={formatDate(customer.createdAt)} color="purple" />
                    {customer.sponsorCode && (
                      <InfoCard icon={FiAward} label="Sponsor Code" value={customer.sponsorCode} color="pink" />
                    )}
                  </div>
                </div>

                {/* Address Information Section */}
                <div className="mb-8">
                  <SectionHeader icon={FiMapPin} title="Address Information" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoCard icon={FaCity} label="City" value={customer.city || 'Not provided'} color="cyan" />
                    <InfoCard icon={FaBuilding} label="State" value={customer.state || 'Not provided'} color="teal" />
                    <div className="md:col-span-2">
                      <InfoCard icon={FiHome} label="Complete Address" value={customer.customerAddress || 'Not provided'} color="gray" />
                    </div>
                  </div>
                </div>

                {/* Additional Information Section */}
                {(customer.gstNumber || customer.panNumber || customer.referralCode) && (
                  <div className="mb-8">
                    <SectionHeader icon={FiShield} title="Additional Information" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {customer.gstNumber && (
                        <InfoCard icon={FiGlobe} label="GST Number" value={customer.gstNumber} color="orange" />
                      )}
                      {customer.panNumber && (
                        <InfoCard icon={FaIdCard} label="PAN Number" value={customer.panNumber} color="red" />
                      )}
                      {customer.referralCode && (
                        <InfoCard icon={FiStar} label="Referral Code" value={customer.referralCode} color="yellow" />
                      )}
                    </div>
                  </div>
                )}

                {/* Properties Section */}
                {customer.properties && customer.properties.length > 0 && (
                  <div className="mb-4">
                    <SectionHeader icon={FiHome} title="Properties" count={customer.properties.length} />
                    <div className="space-y-3">
                      {customer.properties.map((property, index) => (
                        <div
                          key={property._id || index}
                          className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:shadow-md transition-all"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                                <FiHome className="w-4 h-4 text-indigo-600" />
                              </div>
                              <h4 className="font-semibold text-gray-800">{property.title || `Property ${index + 1}`}</h4>
                            </div>
                            <span className="text-sm font-bold text-indigo-600">₹{property.price?.toLocaleString() || 'N/A'}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-xs text-gray-500">Location</p>
                              <p className="text-gray-700">{property.location || property.address || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Area</p>
                              <p className="text-gray-700">{property.area || property.size || 'N/A'} sq.ft.</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-xs text-gray-500">Status</p>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                property.status === 'sold' 
                                  ? 'bg-red-100 text-red-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {property.status === 'sold' ? 'Sold' : 'Available'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Close
                </button>
                <button className="px-5 py-2.5 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all">
                  Send Message
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CustomerDetailsModal;

// Add this style to your global CSS or component
const styles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #c7d2fe;
    border-radius: 3px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #818cf8;
  }
`;